/**
 * Optimization Utilities Index
 * 
 * This file exports all optimization-related utilities for easier imports.
 * It allows importing multiple optimization tools with a single import statement.
 * 
 * Example:
 * import { 
 *   cacheManager,
 *   enhancedApi, 
 *   performanceMonitor,
 *   imageOptimizer,
 *   useOptimizations
 * } from './utils/optimizations';
 */

import { cacheManager, apiCache, calculationCache } from './cacheManager';
import enhancedApi from './enhancedApi';
import { performanceMonitor } from './performanceMonitor';
import * as imageOptimizer from './imageOptimizer';
import * as useOptimizations from '../hooks/useOptimizations';
import lcpOptimizations from './lcpOptimizations';
import companyDataManager from './companyDataManager';
import apiPerformanceMonitor from './apiPerformanceMonitor';
import simpleApiMonitor from './simpleApiMonitor';
import performanceHub from './performanceHub';

// Export everything
export {
  // Core caching utilities
  cacheManager,
  apiCache,
  calculationCache,
  
  // API enhancements
  enhancedApi,
  companyDataManager,
  
  // Performance monitoring
  performanceMonitor,
  apiPerformanceMonitor,
  simpleApiMonitor,
  performanceHub,
  
  // Image optimization
  imageOptimizer,
  
  // React optimization hooks
  useOptimizations,
  
  // LCP optimizations
  lcpOptimizations
};

/**
 * Initialize all optimization systems
 * Call this function once at app startup
 */
export const initializeOptimizations = () => {
  try {
    // Use the performance hub for unified monitoring
    let performanceCleanup = null;
    try {
      performanceCleanup = performanceHub.initialize();
    } catch (error) {
      console.error('Error initializing performance monitoring:', error);
    }
    
    // Apply LCP optimizations (highest priority for Core Web Vitals)
    let lcpCleanup = null;
    try {
      lcpCleanup = lcpOptimizations.applyLCPOptimizations();
    } catch (error) {
      console.error('Error applying LCP optimizations:', error);
    }
    
    console.log('🚀 Performance optimizations initialized');
    
    // Return cleanup function
    return () => {
      try {
        if (performanceCleanup && typeof performanceCleanup === 'function') performanceCleanup();
      } catch (err) {
        console.error('Error cleaning up performance monitoring:', err);
      }
      
      try {
        if (lcpCleanup && typeof lcpCleanup === 'function') lcpCleanup();
      } catch (err) {
        console.error('Error cleaning up LCP optimizations:', err);
      }
    };
  } catch (error) {
    console.error('Error in initializeOptimizations:', error);
    return () => {}; // Return empty cleanup function
  }
};

// Create a default export object for convenience
const optimizations = {
  cacheManager,
  enhancedApi,
  performanceMonitor,
  imageOptimizer,
  useOptimizations,
  lcpOptimizations,
  initializeOptimizations,
  
  // Runtime optimization debug tools
  getRuntimeStatus() {
    return {
      api: enhancedApi.getRuntimeOptimizationStatus ? enhancedApi.getRuntimeOptimizationStatus() : 'Not available',
      cache: apiCache.getRuntimeOptimizationStatus ? apiCache.getRuntimeOptimizationStatus() : 'Not available',
      performance: performanceMonitor.getMetrics(),
      environment: {
        isProd: process.env.NODE_ENV === 'production',
        isDev: process.env.NODE_ENV === 'development'
      }
    };
  }
};

// Expose runtime status tool to console in production for debugging
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
  // Create CareerChamp namespace if it doesn't exist
  window.CareerChamp = window.CareerChamp || {};
  
  // Add runtime optimization tools
  window.CareerChamp.getRuntimeStatus = optimizations.getRuntimeStatus;
  
  // Add a visual indicator for runtime optimization status
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    console.log('🚀 Runtime optimization tools available via CareerChamp.getRuntimeStatus()');
  } else {
    window.addEventListener('DOMContentLoaded', () => {
      console.log('🚀 Runtime optimization tools available via CareerChamp.getRuntimeStatus()');
    });
  }
}

export default optimizations;